'use client';

import { getLinkType, LinkType } from '@/utils/getLinkType';
import type { CX } from '@crown3/cx';
import classNames from '@crown3/cx';
import NextLink from 'next/link';
import type {
  AnchorHTMLAttributes,
  CSSProperties,
  PropsWithChildren,
} from 'react';
import { forwardRef } from 'react';
import { ArrowUpIcon } from 'xyz-icon-set-react/dist/icons/ArrowUpIcon';
import type { ResponsiveProperty } from '../../utils/responsive';
import { resolveResponsive } from '../../utils/responsive';
import linkStyles from './Link.module.css';
import styles from './typography.module.css';
import type { TypographyProps } from './utils/typographyHelper';
import { typographyInline } from './utils/typographyHelper';

const cx: CX = classNames.bind({ ...styles, ...linkStyles });

export interface LinkProps
  extends
    TypographyProps,
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | 'style'> {
  accent?: boolean;
  display?: ResponsiveProperty<CSSProperties['display']>;
  print?: 'label' | 'href';
  printHref?: string;
}

export const Link = forwardRef<HTMLAnchorElement, PropsWithChildren<LinkProps>>(
  (props, ref) => {
    const {
      textDecoration,
      accent,
      display,
      className,
      children,
      href,
      target,
      rel,
      download,
      print = 'label',
      printHref,
      ...anchorProps
    } = props;
    const style: CSSProperties = {
      ...typographyInline({ ...props, textDecoration }),
      display: resolveResponsive(display),
    };
    const linkType = href ? getLinkType(href) : LinkType.relative;
    const isExternal = [
      LinkType.absolute,
      LinkType.mailto,
      LinkType.document,
    ].includes(linkType);

    const content = (
      <>
        <span className={linkStyles.label}>{children}</span>
        {isExternal && (
          <ArrowUpIcon
            aria-hidden={true}
            className={cx(['icon', 'externalIcon'])}
          />
        )}
      </>
    );
    const linkProps = {
      ...anchorProps,
      ref,
      className: cx(['text', 'link', { accent }], className),
      style,
      target: target ? target : isExternal ? '_blank' : target,
      rel: isExternal
        ? ['noopener', 'noreferrer', rel].filter(Boolean).join(' ')
        : rel,
      download,
      ['data-print']: print,
      ['data-print-href']: printHref ?? href,
    };

    if (linkType === LinkType.relative && href) {
      return (
        <NextLink {...linkProps} href={href}>
          {content}
        </NextLink>
      );
    }

    return (
      <a {...linkProps} href={href}>
        {content}
      </a>
    );
  }
);

Link.displayName = 'Link';
